<script>

  import { onMount } from 'svelte';
  import { External, M } from '../../../external';
  import Loader from '../../../Common/Loader.svelte';
  import { makeLmsRequest } from '../../service/LmsClient';
  import { calcGroupingForTimeRange, generateCanvasGradient, makeWebsiteRequest, months, monthsShort } from '../../../Common/tools';
  import Chart from 'svelte-chartjs/src/Bar.svelte';
  import PageAnalyticsSummary from './PageAnalyticsSummary.svelte';
  import CircleAnalyticsSummary from './CircleAnalyticsSummary.svelte';
  import { ORDER_FILTER_TYPE } from '../../../Filter/static';
  import { LmsUtils } from '../../utils/LmsUtils';

  export let entity = {},
    circleDetails = {},
    activePeriod = {},
    type = 'CIRCLE',
    productIds = [];

  let grouping = 'daily';
  let loading = true;

  const pinkGradientColors = ['#FF55B8', '#FF8787'];
  const blueGradientColors = ['#3da6ff', '#257cc6'];
  const orangeGradientColors = ['#ff822f', '#f69c64'];
  let chartDataLabels = [];
  let monthLabels = [];
  let showMonthsBelow = false;
  let chartWrapper = null;

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: false,
      title: false,
      tooltip: {
        mode: 'index',
        intersect: false,
        displayColors: false
      }
    },
    scales: {
      xAxes: {
        display: false
      },
      yAxisRevenue: {
        type: 'linear',
        position: 'left',
        display: false
      },
      yAxisEnrolled: {
        type: 'linear',
        position: 'right',
        display: false
      },
      yAxisCompleted: {
        type: 'linear',
        position: 'right',
        display: false
      }
    },
    layout: {
      padding: 3
    }
  };

  let chartData = {
    labels: [],
    datasets: []
  };

  let salesChartValues = [];
  let circleChartValues = [];
  let salesStats = {};
  let itemStats = {};
  let subscriptionsStats = {};

  let getOrderStats = async (productIds, from, to, grouping) => {
    let filters = [
      {
        func: 'is',
        type: External.CRM_FILTER_TYPE.ORDER_PROPERTY,
        id: ORDER_FILTER_TYPE.PRODUCT,
        value: [-987, ...productIds].join(',') // just ensuring we always have a product id. This will return empty stats if productIds is empty.
        // If there is no productId and we pass an empty array as value, we won't filter products and will get all store sales...
      }
    ];


    let orderStatsSalesResponse = await makeWebsiteRequest({
      action: 'getOrderStats',
      method: 'post',
      data: {
        grouping: grouping || 'daily',
        type: 'sales',
        from: from,
        to: to,
        filters: JSON.stringify(filters)
      }
    });

    let orderStatsSubscriptionsResponse = await makeWebsiteRequest({
      action: 'getOrderStats',
      method: 'post',
      data: {
        grouping: grouping || 'daily',
        type: 'subscriptions',
        from: from,
        to: to,
        filters: JSON.stringify(filters),
        search: 'includeTotalMrr'
      }
    });

    return {
      orderStatsSalesResponse: orderStatsSalesResponse.data,
      orderStatsSubscriptionsResponse: orderStatsSubscriptionsResponse.data
    };
  };

  let load = async () => {
    let period = LmsUtils.getStartEndTime(activePeriod);
    grouping = calcGroupingForTimeRange(period.fromTime, period.fromTime, period.toTime);
    let url = ['/analytics/circle?from=', period.from, '&to=', period.to, '&grouping=', grouping, '&id=', entity?.id || '', '&type=', type].join('');
    let response = await Promise.all([makeLmsRequest(url), getOrderStats(productIds, period.from, period.to, grouping)]);

    itemStats = await response[0].json();
    salesStats = response[1]?.orderStatsSalesResponse;
    subscriptionsStats = response[1]?.orderStatsSubscriptionsResponse;

    if (!salesStats) {
      loading = false;
      return;
    }

    let dataset = salesStats.groupedSales || {};
    chartDataLabels = Object.keys(itemStats.grouped);
    monthLabels = [];
    showMonthsBelow = chartDataLabels.length > (chartWrapper.offsetWidth > 610 ? 14 : 7);
    chartDataLabels = chartDataLabels.map((label) => {
      let date;
      switch (grouping) {
        case 'hourly':
          let hour = External.WebPlatform.parse(label);
          if (External.WebsiteSettings.getDateFormat() === 'uk') {
            return hour;
          }
          if (hour === 0) {
            return '12';
          }
          if (hour > 12) {
            return hour - 12;
          }
          return hour;
        case 'daily':
          date = new Date(label);
          if (showMonthsBelow) {
            let month = months[date.getMonth()];
            if (monthLabels.length === 0 || monthLabels[monthLabels.length - 1] !== month) {
              monthLabels.push(month);
            }
            return date.getDate();
          } else {
            return External.WebPlatform.dateToStrWithMonth(date, true, false, true, monthsShort);
          }
        case 'monthly':
          date = new Date(label);
          return External.WebPlatform.dateToStrWithMonth(date, false, false, false, monthsShort);
        case 'yearly':
          return label;
      }
      return label;
    });

    salesChartValues = Object.values(dataset);
    circleChartValues = Object.values(itemStats.grouped);
    let orderCountsData = salesChartValues.map((el) => el.first);
    let orderTotalsData = salesChartValues.map((el) => el.second);
    let circleEnrolledCountsData = circleChartValues.map(el => el.enrolled);
    let circleCompletedCountsData = circleChartValues.map(el => el.completed);

    let canvas = document.querySelector('.analytics-box-big canvas');
    let pinkGradient = generateCanvasGradient(canvas, pinkGradientColors);
    let blueGradient = generateCanvasGradient(canvas, blueGradientColors);
    let orangeGradient = generateCanvasGradient(canvas, orangeGradientColors);

    chartData = {
      labels: chartDataLabels,
      datasets: [
        {
          label: M.SALES,
          data: orderTotalsData,
          backgroundColor: pinkGradient,
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: true,
          borderRadius: 3,
          borderColor: 'transparent',
          order: 1,
          yAxisID: 'yAxisRevenue'
        },
        // not showing enrolled in the graph for PAGE stats
        ...(type === 'PAGE' ? [] : [{
          label: M.ENROLLED_MEMBERS,
          data: circleEnrolledCountsData,
          backgroundColor: 'transparent',
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: false,
          borderRadius: 3,
          borderColor: blueGradient,
          order: 0,
          type: 'line',
          yAxisID: 'yAxisEnrolled'
        }]),
        {
          label: M.COMPELTED_MEMBERS,
          data: circleCompletedCountsData,
          backgroundColor: 'transparent',
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0.1,
          lineTension: 0.3,
          fill: false,
          borderRadius: 3,
          borderColor: type === 'PAGE' ? blueGradient : orangeGradient,
          order: 0,
          type: 'line',
          yAxisID: 'yAxisCompleted'
        }
      ]
    };

    loading = false;
  };

  onMount(load);

</script>


<div class="padding-45 f-left col20" style="padding-top: 25px;">
  {#if loading}
    <Loader />
  {:else}
    {#if type === 'PAGE'}
      <PageAnalyticsSummary circleDetails={circleDetails} pageStats={itemStats} salesStats={salesStats} />
    {:else}
      <CircleAnalyticsSummary circleDetails={circleDetails} entity={entity} entityStats={itemStats} salesStats={salesStats} />
    {/if}
  {/if}

  <div class="flex f-left col20 mt-25 mb-25" class:visibility-hidden={loading}>
    <div class="analytics-box-big col20" style="padding-bottom: 0px;">
      <h2 class="col20 f-left f-28 gray-text">{M.MEMBERS_AND_REVENUE}</h2>

      <div class="col20 f-left relative mt-25 modern-chart-box" bind:this={chartWrapper}>
        <Chart data={chartData} options={chartOptions} />
        <div class="axis">
          {#each chartDataLabels as label, i}
            <div class="tick">
              <span class="tick-label">{label}</span>
              {#if type === 'PAGE'}
                <span class="value value-first"><span class="chart-color" style="background: #3da6ff" />{circleChartValues[i].completed}</span>
                <span class="value value-second"><span class="chart-color"
                                                       style="background: #f97f7f" />{External.Tools.currencyToStrWithSymbol(salesChartValues[i].second)}</span>
              {:else}
                <span class="value value-first"><span class="chart-color" style="background: #ff822f" />{circleChartValues[i].completed}</span>
                <span class="value value-second"><span class="chart-color" style="background: #3da6ff" />{circleChartValues[i].enrolled}</span>
                <span class="value value-third"><span class="chart-color"
                                                      style="background: #f97f7f" />{External.Tools.currencyToStrWithSymbol(salesChartValues[i].second)}</span>
              {/if}
            </div>
          {/each}
        </div>
        {#if grouping === "hourly"}
          <div class="col20 f-left flex flex-row chart-label-row">
            <span class="flex-1 ta-center"><p>AM</p></span>
            <span class="flex-1 ta-center"><p>PM</p></span>
          </div>
        {:else if grouping === "daily" && showMonthsBelow}
          <div class="col20 f-left flex flex-row chart-label-row">
            {#each monthLabels as label, i}
              <span
                class="flex-1"
                class:ta-left={i === 0 && monthLabels.length > 1}
                class:ta-right={i === monthLabels.length - 1 && monthLabels.length > 1}
                class:ta-center={monthLabels.length === 1 || (i > 1 && i < monthLabels.length - 1)}>
                <p>{label}</p>
              </span>
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </div>
  {#if !loading}
    <!-- Bottom Stats Lane -->
    <div class="flex col20 mt-35 mb-25 f-left store-section-analytics-warapper flex-col-mobile double-row-analytics" style="background: #f5f6fa;">
      <div class="analytics-box analytics-box-small">
        <h3 class="flex align-center">
          {M.NEW_SUBSCRIBERS}
        </h3>
        <h2 class="col20 f-left f-28 gray-text"><b>{subscriptionsStats?.newActiveSubscriptions || 0}</b></h2>
      </div>
      <div class="analytics-box analytics-box-small">
        <h3 class="flex align-center">
          {M.CANCELATIONS}
          <span class="material-icons-outlined ml-5 stats-info" style="font-size: 20px;">
          info
          <span class="none-soft hidden-info">
            <h4>{M.CANCELATIONS}</h4>
            <span>{M.CANCELATIONS_DESK}</span>
          </span>
        </span>
        </h3>
        <h2 class="col20 f-left f-28 gray-text"><b>{subscriptionsStats.newCanceledSubscriptions || 0}</b></h2>
      </div>
      <div class="analytics-box analytics-box-small">
        <h3 class="flex align-center">
          {M.TOTAL_MRR}
          <span class="material-icons-outlined ml-5 stats-info" style="font-size: 20px;">
          info
          <span class="none-soft hidden-info">
            <h4>{M.MONTHLY_RECURRING_REVENUE}</h4>
            <span>{M.MONTHLY_RECURRING_REVENUE_DESC}</span>
          </span>
        </span>
        </h3>
        <h2 class="col20 f-left f-28 gray-text"><b>{External.Tools.currencyToStrWithSymbol(subscriptionsStats.totalSubscriptionsMrr)}</b>
          <span>({subscriptionsStats.totalActiveSubscriptions} {M.SUBSCRIPTIONS})</span>
        </h2>
      </div>
    </div>
  {/if}
</div>
